import React, { useRef, useState } from "react";
import { Typography } from "..";
import * as styles from "./Input.module.scss";
import cn from "classnames";
import { Colors } from "@helpers/Colors";

export type InputProps = {
	/** Specific class name to give to the Input  */
	inputClassName?: string;
	/** Input label also be placeholder of the Input */
	label?: string;
	/** Input value */
	value: string;
	/** theme of the input (default=false)*/
	lightmode?: boolean;
	/** Called when the value of the input changes */
	onChange: (e: string) => void;
	/** class name to go on the wrapper */
	className?: string;
	/** Apply width 100% to the input */
	fullWidth?: boolean;
	/** Email, name, etc */
	autoComplete?: string;
	/** Placeholder value, will conflict with label if label exists */
	placeholder?: string;
	/** Optional error color */
	inputErrorColor?: Colors;
    inputLabelClass?: string;
	inputWrapperOverwrite?:string
} & ValidationProps;

export default function Input({
	label,
	value,
	onChange,
	lightmode = false,
	inputClassName,
	className,
	fullWidth = false,
	autoComplete,
	placeholder,
	inputErrorColor,
    inputLabelClass,
	inputWrapperOverwrite,
	...validationProps
}: InputProps) {
	const { error, onBlur } = useInputError(value, validationProps);
	return (
		<div
			className={cn(className, styles.genemodInputWrapper, inputWrapperOverwrite,{
				[styles.genemodInputWrapper__lightmode]: lightmode,

			})}
		>
			<div className={styles.genemodInputContainer}>
				<input
					style={{ width: fullWidth ? "100%" : undefined }}
					className={cn(styles.genemodInput, inputClassName, {
						[styles.genemodInput__error]: !!error,
					})}
					value={value}
					onChange={(e) => onChange(e.target.value)}
					placeholder={placeholder || " "}
					onBlur={onBlur}
					autoComplete={autoComplete}
				/>
				{label && <div className={cn(styles.inputLabel, inputLabelClass)}>{label}</div>}
			</div>
			{!!error && (
				<Typography
					variant="CAPTION"
					className={styles.inputError}
					color={inputErrorColor}
				>
					{error}
				</Typography>
			)}
		</div>
	);
}

type InputValidator = {
	validator: (value: string) => boolean;
	error: string;
};

const REQUIRED_VALIDATOR = {
	validator: (value: string) => !!value,
	error: `*Required`,
};

export const validateEmail = (email: string) =>
	new RegExp(
		/[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/
	).test(email || "");

const EMAIL_VALIDATOR = {
	validator: validateEmail,
	error: "Please provide a valid work email",
};

export type ValidationProps = {
	/** A list of local functions that checks the validity of the input value */
	validators?: InputValidator[];
	/** Called when validity value changes, even if is not visible! */
	onValidityChange?: (isValid: boolean) => void;
	/** Show error even when a blur hasn't happened. For when the user clicks submit */
	forceShowError?: boolean;
	/** Helper that adds required validator */
	required?: boolean;
	/** Helper that adds email validator */
  emailValidator?: boolean;
  // Custom error message for required field
  customErrorMessage?: string;
};

export const useInputError = (
	value: string,
	{
		validators: propValidators = [],
		onValidityChange,
		forceShowError = false,
		required = false,
		emailValidator = false,
	}: ValidationProps
) => {
	const [hasBlurred, setHasBlurred] = useState(false);

	let validators = propValidators;
	if (emailValidator) {
		validators = [EMAIL_VALIDATOR, ...propValidators];
	}
	if (required) {
		validators = [REQUIRED_VALIDATOR, ...propValidators];
	}

	const badValidator = validators.find(({ validator }) => !validator(value));

	// Check previous value so we can trigger onValidityChange, runs on first pass
	const isValid = useRef<boolean | undefined>(undefined);
	const newIsValid = !badValidator;
	if (isValid.current !== newIsValid) {
		isValid.current = newIsValid;
		setHasBlurred(false);
		onValidityChange?.(newIsValid);
	}

	const error =
		(forceShowError || hasBlurred) &&
		!!badValidator?.error &&
		badValidator.error;
	return { error, onBlur: () => setHasBlurred(true) };
};
